/**
 * Modal Field Adapter for Therapeutic Areas
 * Transforms field registry to modal field configurations
 */

import { FieldConfig } from '../../../components/modals/ModalFieldRenderer';
import {
  BaseFieldDefinition,
  isDateOnlyField,
} from '../../../shared/types/fieldRegistry';
import { formatDateOnly, formatTimestamp } from '../../../utils/dateConversion';
import { THERAPEUTIC_AREAS_FIELDS_REGISTRY } from '../fields';
import { TherapeuticArea } from '../../../services/api/domains/therapeutic-areas/types';

/**
 * Create a standard field configuration from field definition
 */
function createStandardField(
  field: BaseFieldDefinition<TherapeuticArea>
): FieldConfig<TherapeuticArea> {
  let fieldType: 'text' | 'textarea' | 'number' | 'date' | 'readonly' = 'text';

  if (field.isReadOnly) {
    fieldType = 'readonly';
  } else if (field.dataType === 'number') {
    fieldType = 'number';
  } else if (field.dataType === 'date') {
    fieldType = 'date';
  } else if (field.renderConfig?.formatAs === 'html') {
    fieldType = 'textarea';
  }

  return {
    key: field.key as keyof TherapeuticArea,
    label: field.label,
    type: fieldType,
    isRequired: field.isRequired,
    isAlwaysDisabled: field.isReadOnly,
    getValue: (entity: TherapeuticArea) => {
      const value = entity[field.key as keyof TherapeuticArea];
      // Format dates for read-only display
      if (field.isReadOnly && field.dataType === 'date' && value) {
        return isDateOnlyField(field)
          ? formatDateOnly(value)
          : formatTimestamp(value);
      }
      return value;
    },
    setValue: field.isReadOnly
      ? undefined
      : (entity: TherapeuticArea, value: unknown) => ({
          ...entity,
          [field.key]: value,
        }),
  };
}

/**
 * Transform field registry to modal field configurations
 */
export function toModalFields(): FieldConfig<TherapeuticArea>[] {
  const fields: FieldConfig<TherapeuticArea>[] = [];

  for (const field of THERAPEUTIC_AREAS_FIELDS_REGISTRY) {
    // Skip non-editable and non-readonly fields (e.g., computed fields not shown in modal)
    if (!field.isEditable && !field.isReadOnly) continue;

    // Standard fields (text, number, date, readonly)
    fields.push(createStandardField(field));
  }

  return fields;
}

/**
 * Get fields that should always be disabled in the modal
 */
export function getAlwaysDisabledFields(): string[] {
  return THERAPEUTIC_AREAS_FIELDS_REGISTRY.filter(f => f.isReadOnly).map(
    f => f.key
  );
}
